import { useCallback, useEffect, useRef, useState } from "react";
import { cadClient } from "./cad/client";
import { AboutDialog } from "./components/AboutDialog";
import { Editor } from "./components/Editor";
import { ProjectHome } from "./components/ProjectHome";
import type { MotiveDocument } from "./model/types";
import { loadProject, requestPersistentStorage, saveProject } from "./storage/database";
import { readMotivePackage } from "./storage/motivePackage";
import { useModelStore } from "./state/store";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export default function App(): React.JSX.Element {
  const [showHome, setShowHome] = useState(true);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setDocument = useModelStore((state) => state.setDocument);
  const newDocument = useModelStore((state) => state.newDocument);
  const setSaveStatus = useModelStore((state) => state.setSaveStatus);

  useCadEvaluation(!showHome);
  useAutosave(!showHome);

  useEffect(() => {
    const listener = (event: Event): void => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", listener);
    return () => window.removeEventListener("beforeinstallprompt", listener);
  }, []);

  const openDocument = useCallback((document: MotiveDocument): void => {
    setDocument(document);
    setShowHome(false);
    void requestPersistentStorage();
  }, [setDocument]);

  useEffect(() => {
    const launchQueue = (window as typeof window & {
      launchQueue?: { setConsumer: (consumer: (params: { files: FileSystemFileHandle[] }) => void) => void };
    }).launchQueue;
    launchQueue?.setConsumer((params) => {
      const handle = params.files[0];
      if (!handle) return;
      void handle.getFile().then(readMotivePackage).then(openDocument).catch((error) => {
        setSaveStatus("error", error instanceof Error ? error.message : String(error));
      });
    });
  }, [openDocument, setSaveStatus]);

  const handleFiles = async (files: FileList | null): Promise<void> => {
    const file = files?.[0];
    if (!file) return;
    try {
      openDocument(await readMotivePackage(file));
    } catch (error) {
      setSaveStatus("error", error instanceof Error ? error.message : String(error));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const createNew = (): void => {
    newDocument();
    setShowHome(false);
    void requestPersistentStorage();
  };

  const openRecent = async (id: string): Promise<void> => {
    try {
      openDocument(await loadProject(id));
    } catch (error) {
      setSaveStatus("error", error instanceof Error ? error.message : String(error));
    }
  };

  const install = async (): Promise<void> => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return (
    <>
      <input ref={fileInputRef} className="sr-only" type="file" accept=".motive,application/vnd.modelmotive.project+zip,application/zip" onChange={(event) => void handleFiles(event.target.files)} />
      {showHome ? (
        <ProjectHome
          onNew={createNew}
          onOpenFile={() => fileInputRef.current?.click()}
          onOpenRecent={(id) => void openRecent(id)}
          onOpenTemplate={openDocument}
          installAvailable={Boolean(installPrompt)}
          onInstall={() => void install()}
          onAbout={() => setAboutOpen(true)}
        />
      ) : (
        <Editor
          onHome={() => setShowHome(true)}
          onOpenFile={() => fileInputRef.current?.click()}
          onNew={createNew}
          onAbout={() => setAboutOpen(true)}
        />
      )}
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </>
  );
}

function useCadEvaluation(active: boolean): void {
  const revision = useModelStore((state) => state.revision);
  const document = useModelStore((state) => state.document);
  const setEvaluation = useModelStore((state) => state.setEvaluation);
  const setKernelStatus = useModelStore((state) => state.setKernelStatus);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setKernelStatus("loading", "Loading exact CAD kernel…");
    void cadClient.ready().then(() => {
      if (!cancelled) setKernelStatus("ready", "CAD kernel ready");
    }).catch((error) => {
      if (!cancelled) setKernelStatus("error", error instanceof Error ? error.message : String(error));
    });
    return () => { cancelled = true; };
  }, [active, setKernelStatus]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setKernelStatus("rebuilding", document.nodes.length === 0 ? "Ready for a new solid" : "Rebuilding exact geometry…");
      void cadClient.evaluate(structuredClone(document), revision, "preview")
        .then((result) => { if (!cancelled) setEvaluation(result); })
        .catch((error) => { if (!cancelled) setKernelStatus("error", error instanceof Error ? error.message : String(error)); });
    }, 140);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [active, document, revision, setEvaluation, setKernelStatus]);
}

function useAutosave(active: boolean): void {
  const revision = useModelStore((state) => state.revision);
  const document = useModelStore((state) => state.document);
  const setSaveStatus = useModelStore((state) => state.setSaveStatus);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setSaveStatus("saving", "Saving locally…");
      void saveProject(structuredClone(document)).then(() => {
        if (!cancelled) setSaveStatus("saved", "Saved on this device");
      }).catch((error) => {
        if (!cancelled) setSaveStatus("error", error instanceof Error ? error.message : "Local save failed");
      });
    }, 650);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [active, document, revision, setSaveStatus]);
}
